export interface NativeAuthSubscriptionRegistration {
  readonly markReady: () => void;
  readonly stop: () => void;
}

type RegistrationState = 'pending' | 'ready';

const registrations = new Map<symbol, RegistrationState>();
const deferredReadyActions = new Set<() => void>();

function isReady(): boolean {
  return registrations.size > 0 && [...registrations.values()].every((state) => state === 'ready');
}

function flushReadyActions(): void {
  if (!isReady()) {
    return;
  }
  const actions = [...deferredReadyActions];
  deferredReadyActions.clear();
  for (const action of actions) {
    action();
  }
}

/** Defer ownership-sensitive work until auth events have an active committed consumer. */
export function runWhenNativeAuthSubscriptionReady(action: () => void): void {
  if (isReady()) {
    action();
    return;
  }
  deferredReadyActions.add(action);
}

/** Track one provider bootstrap without treating a cancelled cold owner as ready. */
export function registerNativeAuthSubscription(): NativeAuthSubscriptionRegistration {
  const token = Symbol('native auth subscription');
  let active = true;
  registrations.set(token, 'pending');
  return {
    markReady: () => {
      if (!active) {
        return;
      }
      registrations.set(token, 'ready');
      flushReadyActions();
    },
    stop: () => {
      if (!active) {
        return;
      }
      active = false;
      registrations.delete(token);
      flushReadyActions();
    },
  };
}

export function __resetNativeAuthSubscriptionReadinessForTests(): void {
  registrations.clear();
  deferredReadyActions.clear();
}
